@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginetabular

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import org.w3c.dom.TouchEvent
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

private fun tabularSplitPointerY(event: Event): Double? {
    val mouseEvent = event as? MouseEvent
    if (mouseEvent != null) return mouseEvent.clientY.toDouble()

    val touchEvent = event as? TouchEvent ?: return null
    val touch = touchEvent.touches.item(0) ?: touchEvent.changedTouches.item(0) ?: return null
    return touch.clientY.toDouble()
}

@Composable
fun TabularSplitScreen(
    screen1: @Composable () -> Unit,
    screen2: (@Composable () -> Unit)? = null,
    initialRatio: Float = 0.55f,
    minRatio: Float = 0f,
    maxRatio: Float = 1f,
    handleColor: CSSColorValue = Color("rgba(12, 14, 18, 0.96)"),
    clipContent: Boolean = true
) {
    val hasSecondScreen = screen2 != null

    val storageKey = remember(initialRatio, minRatio, maxRatio) {
        "tabular.split.${initialRatio}.${minRatio}.${maxRatio}"
    }

    fun storedRatio(): Float =
        window.localStorage.getItem(storageKey)
            ?.toFloatOrNull()
            ?.coerceIn(minRatio, maxRatio)
            ?: initialRatio.coerceIn(minRatio, maxRatio)

    var ratio by remember(storageKey) { mutableStateOf(storedRatio()) }
    var dragging by remember { mutableStateOf(false) }
    var containerRef by remember { mutableStateOf<HTMLElement?>(null) }

    fun updateRatio(value: Float) {
        ratio = value.coerceIn(minRatio, maxRatio)
        window.localStorage.setItem(storageKey, ratio.toString())
    }

    if (dragging && hasSecondScreen) {
        DisposableEffect(Unit) {
            val moveHandler: (Event) -> Unit = { e ->
                val clientY = tabularSplitPointerY(e)
                if (clientY != null) containerRef?.let {
                    e.preventDefault()
                    val rect = it.getBoundingClientRect()
                    val newRatio = (clientY - rect.top).toFloat() / rect.height.toFloat()
                    updateRatio(newRatio)
                }
            }
            val upHandler: (Event) -> Unit = { dragging = false }
            window.addEventListener("mousemove", moveHandler)
            window.addEventListener("mouseup", upHandler)
            window.addEventListener("touchmove", moveHandler)
            window.addEventListener("touchend", upHandler)
            window.addEventListener("touchcancel", upHandler)
            onDispose {
                window.removeEventListener("mousemove", moveHandler)
                window.removeEventListener("mouseup", upHandler)
                window.removeEventListener("touchmove", moveHandler)
                window.removeEventListener("touchend", upHandler)
                window.removeEventListener("touchcancel", upHandler)
            }
        }
    }

    Div({
        ref { containerRef = it; onDispose { containerRef = null } }
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            height(100.percent)
            width(100.percent)
            overflow(if (clipContent) "hidden" else "visible")
        }
    }) {
        Div({
            style {
                flex(if (hasSecondScreen) ratio.toString() else "1")
                property("flex-basis", "0")
                overflow(if (clipContent) "hidden" else "visible")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
            }
        }) { screen1() }

        screen2?.let { secondScreen ->
            Div({
                style {
                    height(8.px)
                    margin(12.px, 0.px, 4.px, 0.px)
                    backgroundColor(handleColor)
                    cursor("row-resize")
                    flexShrink(0)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("transition", "background-color 0.2s ease-in-out")
                    property("border-top", "1px solid rgba(255,255,255,0.08)")
                    property("border-bottom", "1px solid rgba(255,255,255,0.08)")
                    property("touch-action", "none")
                    property("user-select", "none")
                }
                onMouseDown { dragging = true; it.preventDefault() }
                onTouchStart {
                    dragging = true
                    it.preventDefault()
                }
            }) {
                Div({
                    style {
                        width(42.px)
                        height(3.px)
                        backgroundColor(Color("rgba(255,255,255,0.92)"))
                        borderRadius(999.px)
                        property("box-shadow", "0 0 8px rgba(255,255,255,0.18)")
                    }
                })
            }

            Div({
                style {
                    flex((1.0f - ratio).toString())
                    property("flex-basis", "0")
                    overflow(if (clipContent) "hidden" else "visible")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                }
            }) { secondScreen() }
        }
    }
}
